import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { useToast } from '../contexts/ToastContext';
import Modal from './Modal';
import {
  User,
  Mail,
  Phone,
  FileText,
  Shield,
  CreditCard,
  Upload,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Camera,
  ExternalLink,
} from 'lucide-react';

const STEPS = [
  { number: 1, label: 'Personal Details', icon: User },
  { number: 2, label: 'License & Banking', icon: CreditCard },
  { number: 3, label: 'ID & Consent', icon: Shield },
];

const DOCUMENT_TYPES = {
  license: { type: 'driver_license', label: "Driver's License Document" },
  idProof: { type: 'id_proof', label: 'ID Proof' },
};

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  driverLicenseNumber: '',
  bankAccount: '',
};

const initialDocuments = {
  license: null,
  idProof: null,
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function FileUploadField({ docKey, document, onUpload, label, required, isEditMode = false }) {
  const inputRef = useRef(null);

  return (
    <div>
      <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {isEditMode && (
        <div className="mb-2 rounded-lg border border-border dark:border-border-dark bg-surface-secondary/80 dark:bg-surface-secondary-dark/80 px-3 py-2.5">
          {document?.existing ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text dark:text-text-dark truncate">{document.fileName}</p>
                  <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark">Previously uploaded document</p>
                </div>
              </div>
              {document.url ? (
                <a
                  href={document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-dark shrink-0"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  View document
                </a>
              ) : (
                <span className="text-xs text-text-tertiary dark:text-text-tertiary-dark shrink-0">Preview link unavailable</span>
              )}
            </div>
          ) : document?.base64Data ? (
            <p className="text-sm text-success">
              <span className="font-medium">New file selected:</span> {document.fileName} (saved when you submit)
            </p>
          ) : (
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
              No document on file for this item yet. Use the upload area below to attach one.
            </p>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(docKey, file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`w-full flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-xl transition-all cursor-pointer ${
          document
            ? 'border-success bg-success/5 dark:bg-success/10'
            : 'border-border dark:border-border-dark hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10'
        }`}
      >
        {document ? (
          <CheckCircle className="w-5 h-5 text-success shrink-0" />
        ) : (
          <Upload className="w-5 h-5 text-text-secondary dark:text-text-secondary-dark shrink-0" />
        )}
        <span
          className={`text-sm truncate ${
            document
              ? 'text-success font-medium'
              : 'text-text-secondary dark:text-text-secondary-dark'
          }`}
        >
          {document
            ? document.existing
              ? `${document.fileName} (on file)`
              : document.fileName
            : `Click to upload (image or PDF)`}
        </span>
      </button>
    </div>
  );
}

function mapApiDocumentsToState(apiDocs) {
  const next = { ...initialDocuments };
  if (!Array.isArray(apiDocs)) return next;
  const byType = {};
  apiDocs.forEach((d) => {
    const t = d.document_type || d.type;
    if (t) byType[t] = d;
  });
  Object.entries(DOCUMENT_TYPES).forEach(([key, { type }]) => {
    const row = byType[type];
    if (row) {
      next[key] = {
        fileName: row.file_name || row.fileName || 'Uploaded',
        mimeType: row.mime_type || 'application/octet-stream',
        type,
        displayName: row.display_name || DOCUMENT_TYPES[key].label,
        existing: true,
        url: row.url,
      };
    }
  });
  return next;
}

export default function AddDriverModal({ isOpen, onClose, mode = 'create', driverId = null }) {
  const isEdit = mode === 'edit' && driverId;
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [documents, setDocuments] = useState(initialDocuments);
  const [profileImage, setProfileImage] = useState(null);
  const [backgroundCheckConsent, setBackgroundCheckConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const profileInputRef = useRef(null);

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: driverRes, isLoading: driverLoading } = useQuery({
    queryKey: ['fleet-driver-edit', driverId],
    queryFn: () => fleetService.getDriver(driverId).then((r) => r.data?.data ?? r.data),
    enabled: Boolean(isOpen && isEdit && driverId),
  });

  useEffect(() => {
    setHydrated(false);
  }, [driverId]);

  useEffect(() => {
    if (!isOpen) {
      setHydrated(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isEdit || !driverRes || hydrated) return;

    const d = driverRes;
    const fdp = d.fleet_driver_profile || {};
    setFormData({
      firstName: d.first_name || '',
      lastName: d.last_name || '',
      email: d.email || '',
      phone: d.phone || '',
      driverLicenseNumber: d.driver_license_number || fdp.driverLicenseNumber || '',
      bankAccount: fdp.bankAccount ?? '',
    });
    setDocuments(mapApiDocumentsToState(d.documents));
    if (d.profile_image_url) {
      setProfileImage({ previewUrl: d.profile_image_url, existing: true });
    } else {
      setProfileImage(null);
    }
    setBackgroundCheckConsent(
      typeof fdp.backgroundCheckConsent === 'boolean' ? fdp.backgroundCheckConsent : true
    );
    setHydrated(true);
  }, [isOpen, isEdit, driverRes, hydrated]);

  const saveMutation = useMutation({
    mutationFn: ({ payload, edit }) =>
      edit ? fleetService.updateDriver(driverId, payload) : fleetService.createDriver(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['fleet-drivers']);
      if (driverId) {
        queryClient.invalidateQueries(['fleet-driver', driverId]);
        queryClient.invalidateQueries(['fleet-driver-edit', driverId]);
      }
      showSuccess(
        variables.edit
          ? 'Driver updated successfully.'
          : "Driver registered successfully. Documents submitted for admin approval. The driver's login credentials will be sent via email after approval."
      );
      handleClose();
    },
    onError: (error) => {
      showError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          (isEdit ? 'Failed to update driver' : 'Failed to register driver')
      );
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleDocumentUpload = async (docKey, file) => {
    try {
      const base64Data = await fileToBase64(file);
      setDocuments((prev) => ({
        ...prev,
        [docKey]: {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          base64Data,
          type: DOCUMENT_TYPES[docKey].type,
          displayName: DOCUMENT_TYPES[docKey].label,
        },
      }));
    } catch {
      showError('Failed to read the selected file. Please try again.');
    }
  };

  const handleProfileImageUpload = async (file) => {
    try {
      const base64Data = await fileToBase64(file);
      setProfileImage({ fileName: file.name, mimeType: file.type, size: file.size, base64Data, previewUrl: URL.createObjectURL(file) });
    } catch {
      showError('Failed to read the profile picture. Please try again.');
    }
  };

  const hasUploadedDoc = (key) => {
    const doc = documents[key];
    return doc && (doc.base64Data || doc.existing);
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!isEdit) {
        const em = formData.email.trim();
        if (!em) newErrors.email = 'Email is required';
        else if (!em.includes('@')) newErrors.email = 'Email must contain @';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    }
    if (step === 2) {
      if (!formData.driverLicenseNumber.trim()) newErrors.driverLicenseNumber = "License number is required";
      if (!hasUploadedDoc('license')) newErrors.licenseDoc = "License document is required";
      if (!formData.bankAccount.trim()) newErrors.bankAccount = "Bank account is required";
    }
    if (step === 3) {
      if (!hasUploadedDoc('idProof')) newErrors.idProof = "ID proof document is required";
      if (!backgroundCheckConsent) newErrors.backgroundCheckConsent = "Background check consent is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrevious = () => setCurrentStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    const documentPayload = Object.values(documents)
      .filter(Boolean)
      .filter((doc) => doc.base64Data)
      .map((doc) => ({
        type: doc.type,
        displayName: doc.displayName,
        fileName: doc.fileName,
        mimeType: doc.mimeType,
        fileSize: doc.size,
        base64Data: doc.base64Data,
      }));

    const profilePayload =
      profileImage && profileImage.base64Data
        ? {
            fileName: profileImage.fileName,
            mimeType: profileImage.mimeType,
            base64Data: profileImage.base64Data,
            fileSize: profileImage.size,
          }
        : undefined;

    const driverProfile = {
      driverLicenseNumber: formData.driverLicenseNumber,
      bankAccount: formData.bankAccount,
      backgroundCheckConsent,
    };

    if (isEdit) {
      const updatePayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        profilePicture: profilePayload,
        driverProfile,
        documents: documentPayload,
      };
      saveMutation.mutate({ payload: updatePayload, edit: true });
      return;
    }

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      profilePicture: profilePayload,
      driverProfile,
      documents: Object.values(documents)
        .filter(Boolean)
        .map((doc) => ({
          type: doc.type,
          displayName: doc.displayName,
          fileName: doc.fileName,
          mimeType: doc.mimeType,
          fileSize: doc.size,
          base64Data: doc.base64Data,
        })),
    };

    saveMutation.mutate({ payload, edit: false });
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(initialFormData);
    setDocuments(initialDocuments);
    setProfileImage(null);
    setBackgroundCheckConsent(false);
    setErrors({});
    setHydrated(false);
    onClose();
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
      errors[field] ? 'border-danger' : 'border-border dark:border-border-dark'
    }`;

  const renderStep1 = () => (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-text dark:text-text-dark flex items-center gap-2">
        <User className="w-5 h-5 text-primary" />
        Personal Details
      </h3>

      <div className="flex flex-col items-center gap-3">
        <input
          ref={profileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleProfileImageUpload(file);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => profileInputRef.current?.click()}
          className="relative w-20 h-20 rounded-full border-2 border-dashed border-border dark:border-border-dark hover:border-primary overflow-hidden transition-all bg-surface-secondary dark:bg-surface-secondary-dark flex items-center justify-center"
        >
          {profileImage ? (
            <img src={profileImage.previewUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <User className="w-8 h-8 text-text-secondary dark:text-text-secondary-dark" />
          )}
          <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
        </button>
        <button
          type="button"
          onClick={() => profileInputRef.current?.click()}
          className="text-sm text-primary hover:underline"
        >
          {profileImage ? 'Change Photo' : 'Add Profile Photo'} (Optional)
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
            First Name <span className="text-danger">*</span>
          </label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" className={inputClass('firstName')} />
          {errors.firstName && <p className="text-xs text-danger mt-1">{errors.firstName}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
            Last Name <span className="text-danger">*</span>
          </label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" className={inputClass('lastName')} />
          {errors.lastName && <p className="text-xs text-danger mt-1">{errors.lastName}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-2 flex items-center gap-1">
            <Mail className="w-4 h-4" /> Email {!isEdit && <span className="text-danger">*</span>}
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="driver@example.com"
            className={inputClass('email')}
            disabled={isEdit}
            readOnly={isEdit}
          />
          {errors.email && <p className="text-xs text-danger mt-1">{errors.email}</p>}
          {isEdit && (
            <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark mt-1">Email cannot be changed here.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-2 flex items-center gap-1">
            <Phone className="w-4 h-4" /> Phone <span className="text-danger">*</span>
          </label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1234567890" className={inputClass('phone')} />
          {errors.phone && <p className="text-xs text-danger mt-1">{errors.phone}</p>}
        </div>
      </div>

      {!isEdit && (
        <div className="bg-info-light dark:bg-info-dark/20 border border-info/30 rounded-xl p-4">
          <p className="text-sm text-info dark:text-info-dark">
            <strong>Note:</strong> A secure password will be automatically generated for this driver.
            The password will be sent via email after admin approval.
          </p>
        </div>
      )}
      <div className="bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl p-4">
        <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
          Vehicle registration, insurance, and fleet vehicles are managed separately under <strong>Vehicles</strong>. Assign an approved vehicle to this driver after both are approved.
        </p>
      </div>
      {isEdit && (
        <div className="bg-info-light dark:bg-info-dark/20 border border-info/30 rounded-xl p-4">
          <p className="text-sm text-info dark:text-info-dark">
            Update driver details and upload new documents where needed. Existing files stay on record unless you upload a replacement.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-text dark:text-text-dark flex items-center gap-2">
        <FileText className="w-5 h-5 text-primary" />
        Driver&apos;s License & Banking
      </h3>

      <div>
        <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
          Driver&apos;s License Number <span className="text-danger">*</span>
        </label>
        <input type="text" name="driverLicenseNumber" value={formData.driverLicenseNumber} onChange={handleChange} placeholder="DL123456" className={inputClass('driverLicenseNumber')} />
        {errors.driverLicenseNumber && <p className="text-xs text-danger mt-1">{errors.driverLicenseNumber}</p>}
      </div>

      <div>
        <FileUploadField
          docKey="license"
          document={documents.license}
          onUpload={handleDocumentUpload}
          label="Upload License Document"
          required
          isEditMode={isEdit}
        />
        {errors.licenseDoc && <p className="text-xs text-danger mt-1">{errors.licenseDoc}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-text dark:text-text-dark mb-2 flex items-center gap-1">
          <CreditCard className="w-4 h-4" /> Bank Account <span className="text-danger">*</span>
        </label>
        <input type="text" name="bankAccount" value={formData.bankAccount} onChange={handleChange} placeholder="Account number or IBAN" className={inputClass('bankAccount')} />
        {errors.bankAccount && <p className="text-xs text-danger mt-1">{errors.bankAccount}</p>}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-text dark:text-text-dark flex items-center gap-2">
        <Shield className="w-5 h-5 text-primary" />
        ID Proof & Background Check
      </h3>

      <div>
        <FileUploadField
          docKey="idProof"
          document={documents.idProof}
          onUpload={handleDocumentUpload}
          label="Upload ID Proof (National ID / Passport)"
          required
          isEditMode={isEdit}
        />
        {errors.idProof && <p className="text-xs text-danger mt-1">{errors.idProof}</p>}
      </div>

      <div
        className={`flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all ${
          backgroundCheckConsent
            ? 'border-success bg-success/5 dark:bg-success/10'
            : errors.backgroundCheckConsent
            ? 'border-danger bg-danger/5'
            : 'border-border dark:border-border-dark hover:border-primary'
        }`}
        onClick={() => {
          setBackgroundCheckConsent((v) => !v);
          if (errors.backgroundCheckConsent) setErrors((prev) => ({ ...prev, backgroundCheckConsent: null }));
        }}
      >
        <div
          className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
            backgroundCheckConsent
              ? 'bg-success border-success'
              : 'border-border dark:border-border-dark'
          }`}
        >
          {backgroundCheckConsent && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <p className="text-sm text-text dark:text-text-dark leading-relaxed">
          I confirm that this driver consents to a background check as part of the registration process.
          By registering this driver, I acknowledge they have agreed to the{' '}
          <span className="text-primary font-medium">Terms of Service</span> and{' '}
          <span className="text-primary font-medium">Privacy Policy</span>.
        </p>
      </div>
      {errors.backgroundCheckConsent && (
        <p className="text-xs text-danger -mt-3">{errors.backgroundCheckConsent}</p>
      )}

      <div className="bg-warning-light dark:bg-warning-dark/20 border border-warning/30 rounded-xl p-4">
        <p className="text-sm text-warning-dark dark:text-warning font-medium mb-1">Documents submitted for admin review</p>
        <p className="text-xs text-text-secondary dark:text-text-secondary-dark">
          Once submitted, an admin will review the driver&apos;s documents and approve or reject the registration.
          The driver will receive login credentials via email upon approval.
        </p>
      </div>
    </div>
  );

  const stepContent = [renderStep1, renderStep2, renderStep3];

  if (isOpen && isEdit && driverLoading && !driverRes) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Edit Driver" size="lg">
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Edit Driver' : 'Register New Driver'} size="lg">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isCompleted = currentStep > step.number;
            const isActive = currentStep === step.number;
            return (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                      isCompleted
                        ? 'bg-success text-white'
                        : isActive
                        ? 'bg-primary text-white shadow-lg shadow-primary/30'
                        : 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-secondary dark:text-text-secondary-dark'
                    }`}
                  >
                    {isCompleted ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <span
                    className={`text-xs font-medium hidden sm:block ${
                      isActive ? 'text-primary' : isCompleted ? 'text-success' : 'text-text-secondary dark:text-text-secondary-dark'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 rounded transition-all ${
                      currentStep > step.number ? 'bg-success' : 'bg-border dark:bg-border-dark'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="h-1.5 bg-surface-secondary dark:bg-surface-secondary-dark rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>
        <p className="text-xs text-text-secondary dark:text-text-secondary-dark mt-1">
          Step {currentStep} of {STEPS.length}
        </p>
      </div>

      <div className="min-h-[320px]">{stepContent[currentStep - 1]()}</div>

      <div className="flex justify-between gap-3 pt-5 mt-5 border-t border-border dark:border-border-dark">
        <button
          type="button"
          onClick={currentStep === 1 ? handleClose : handlePrevious}
          className="flex items-center gap-2 px-5 py-3 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors font-medium"
        >
          <ChevronLeft className="w-4 h-4" />
          {currentStep === 1 ? 'Cancel' : 'Previous'}
        </button>

        {currentStep < STEPS.length ? (
          <button
            type="button"
            onClick={handleNext}
            className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl font-medium"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl font-medium"
          >
            <CheckCircle className="w-4 h-4" />
            {saveMutation.isPending ? 'Saving...' : isEdit ? 'Save changes' : 'Submit Registration'}
          </button>
        )}
      </div>
    </Modal>
  );
}
